// src/components/homepage/HomePageCommitment.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Trans } from 'react-i18next';
import { Heart, Users, BarChart3, Award } from 'lucide-react';
import {Button} from '@dopomogai/ui';
import {Badge} from '@dopomogai/ui';

interface CommitmentData {
  headline: string;
  text: string;
  pillar1: string;
  pillar2: string;
  pillar3: string;
  cta: string;
}

interface HomePageCommitmentProps {
  commitmentData: CommitmentData;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
  t: (key: string, options?: any) => string;
}

export const HomePageCommitment: React.FC<HomePageCommitmentProps> = ({ commitmentData, onAction, id, t }) => {
  const badgeVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15 + 0.4, 
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  const sectionHoverProps = {
    boxShadow: '0px 0px 30px 0px rgba(110, 231, 183, 0.4)',
    transition: { duration: 0.3, ease: 'easeOut' },
  };

  return (
    <section id={id} className="py-16 md:py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          whileHover={sectionHoverProps}
          className="bg-emerald-600 text-white p-8 md:p-12 rounded-2xl border-2 border-emerald-800 text-center transition-shadow"
        >
          <Award size={48} className="mx-auto mb-6 text-emerald-300" />
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            {commitmentData.headline}
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 max-w-4xl mx-auto text-lg md:text-xl text-emerald-100 leading-relaxed"
          >
            {/* --- THIS IS THE FIX --- */}
            {/* The `t={t}` prop has been removed. The component will correctly use the `t` function */}
            {/* from the i18next context, which is what it expects. */}
            <Trans
              i18nKey={commitmentData.text}
              components={[<strong className="text-white font-semibold" />]}
              defaults={commitmentData.text}
            />
          </motion.p>

          <div className="mt-10 mb-12 flex flex-wrap justify-center items-center gap-4">
            {[
              { text: commitmentData.pillar1, icon: 'heart', color: 'pink' },
              { text: commitmentData.pillar2, icon: 'users', color: 'cyan' },
              { text: commitmentData.pillar3, icon: 'trendingup', color: 'lime' },
            ].map((pillar, index) => (
              <motion.div
                key={pillar.text}
                custom={index}
                variants={badgeVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <Badge
                  text={pillar.text}
                  icon={pillar.icon as any} 
                  accentColor={pillar.color as any} 
                  className="!border-emerald-900 !text-sm"
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex justify-center"
          >
            <Button
              text={commitmentData.cta}
              variant="secondary"
              size="lg"
              onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/impact' })} // Corrected path
              className={
                "bg-white text-emerald-700 border-2 border-emerald-900 " +
                "hover:bg-emerald-50 hover:shadow-[4px_4px_0_0_#ffffff]"
              }
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};